import { useCallback, useEffect } from 'react';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import BrandSlice from '$store/modules/brand/brand.reducer';
import type { CurrencyOutput } from '$store/modules/currencies';

const CURRENCY_STORAGE_KEY = 'simera_selected_currency';

export const useCurrency = () => {
  const dispatch = useAppDispatch();
  const { currencyCode } = useAppSelector((state) => state.brand);

  const applyToStore = useCallback(
    (c: CurrencyOutput) => {
      dispatch(
        BrandSlice.actions.updateCurrency({
          currency: c.Symbol,
          currencyCode: c.Code,
          usdToCurrencyConverter: (amount: number) =>
            c.Code === 'USD'
              ? Math.round(amount * c.Ratio)
              : Math.ceil((amount * c.Ratio) / 50) * 50,
          currencyToUsdConverter: (amount: number) =>
            Math.round(amount / c.Ratio),
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (stored) {
      try {
        applyToStore(JSON.parse(stored) as CurrencyOutput);
      } catch {
        localStorage.removeItem(CURRENCY_STORAGE_KEY);
      }
    }
  }, [applyToStore]);

  const selectCurrency = useCallback(
    (c: CurrencyOutput) => {
      localStorage.setItem(CURRENCY_STORAGE_KEY, JSON.stringify(c));
      applyToStore(c);
    },
    [applyToStore]
  );

  return { selectCurrency, selectedCode: currencyCode };
};
